/**
 * W1-11b (app-shell-ui): the clipboard service (`services.clipboard`; contract ClipboardService, DESIGN.md §5.4).
 *
 * - `set(payload)` keeps the payload in memory (instant, exact) and writes it to the system clipboard as text in the
 *   `PHIEDIT-CLIP/1 <json>` format (core/commands `serializeClipboard`), so a copy reaches other tabs, other projects
 *   and other difficulties. A refused system write (no permission, insecure context, unfocused page) is ignored:
 *   the in-memory payload still pastes in this tab.
 * - `read()` reads the system clipboard: our own last write gives back the in-memory payload (same object); another
 *   tab's copy is parsed (`parseClipboardText`); anything else (plain text copied elsewhere, a denied read) falls
 *   back to the in-memory payload, as the contract says.
 * - `subscribe` notifies Paste buttons and menus when the in-memory payload changes.
 */

import type { ClipboardPayload } from '../core/commands/types';
import type { Unsubscribe } from '../core/util/types';
import { Core } from '../core/commands';
import type { ClipboardService } from './registry/types';

/** The part of the async Clipboard API the service uses. */
export type SystemClipboard = Pick<Clipboard, 'readText' | 'writeText'>;

export interface ClipboardServiceDeps {
  /** System clipboard (default navigator.clipboard when present; null = in-memory only). */
  readonly system?: SystemClipboard | null;
  readonly serialize?: (p: ClipboardPayload) => string;
  readonly parse?: (text: string) => ClipboardPayload | null;
  /** System clipboard failures (default: ignored after one console note per kind). */
  readonly onSystemError?: (op: 'read' | 'write', error: unknown) => void;
}

export interface ShellClipboardService extends ClipboardService {
  subscribe(cb: () => void): Unsubscribe;
  /** Forget the in-memory payload (the system clipboard is left alone). */
  clear(): void;
}

function defaultSystem(): SystemClipboard | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as { readonly clipboard?: Clipboard }).clipboard ?? null;
}

export function createClipboardService(deps: ClipboardServiceDeps = {}): ShellClipboardService {
  const system = deps.system === undefined ? defaultSystem() : deps.system;
  const serialize = deps.serialize ?? ((p: ClipboardPayload) => Core.clipboard.serializeClipboard(p));
  const parse = deps.parse ?? ((t: string) => Core.clipboard.parseClipboardText(t));
  const noted = new Set<string>();
  const onSystemError =
    deps.onSystemError ??
    ((op: 'read' | 'write', err: unknown) => {
      if (noted.has(op)) return;
      noted.add(op);
      console.info(`clipboard: system ${op} unavailable (${err instanceof Error ? err.message : String(err)})`);
    });
  const listeners = new Set<() => void>();
  let payload: ClipboardPayload | null = null;
  /** Text of our last system write (recognises our own copy when reading back). */
  let written: string | null = null;

  const notify = (): void => {
    for (const cb of [...listeners]) cb();
  };

  return {
    get payload() {
      return payload;
    },
    async set(p) {
      payload = p;
      notify();
      if (system === null) return;
      let text: string;
      try {
        text = serialize(p);
      } catch (err) {
        onSystemError('write', err);
        return;
      }
      written = text;
      try {
        await system.writeText(text);
      } catch (err) {
        onSystemError('write', err);
      }
    },
    async read() {
      if (system === null) return payload;
      let text: string;
      try {
        text = await system.readText();
      } catch (err) {
        onSystemError('read', err);
        return payload;
      }
      if (written !== null && text === written) return payload;
      let parsed: ClipboardPayload | null;
      try {
        parsed = parse(text);
      } catch {
        parsed = null;
      }
      return parsed ?? payload;
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    clear() {
      if (payload === null) return;
      payload = null;
      written = null;
      notify();
    },
  };
}
